// Shared PDF byte cache + hook.
//
// Why: the HIL viewers previously handed pdf.js a bare `{ url }` source. That made
// pdf.js own the fetch AND the parse lifecycle inside its loading task. When a
// component remounted mid-load (session switch, center-panel re-publish), react-pdf
// destroyed the in-flight task (node_modules/react-pdf/dist/Document.js —
// `return () => loadingTask.destroy()`), and pdf.js then silently ignored both the
// resolve and the reject (`if (loadingTask.destroyed) return;`). Result: the viewer
// sat on "Loading PDF..." forever — zero network (bytes served from the browser
// HTTP cache), zero console output.
//
// The fix: fetch the signed URL to an ArrayBuffer ONCE per document, ourselves, and
// hand react-pdf `{ data }` — a private COPY of the cached bytes per <Document>,
// because pdf.js detaches (transfers) whatever buffer it parses (see the copy note
// in usePdfDocumentSource). The module-level cache below means a revisit is an
// instant in-memory hand-off (our "cached" instead of the browser's), and every
// load has a real error path the component can surface. A 30MB S3 PDF also stops
// being re-fetched twice by the same screen (DocumentSplitEditor renders the
// document both as a thumbnail tree and inside the side panel).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// LRU cap: a reviewer realistically has 2-3 statements open; 3 x ~30MB stays under
// 100MB resident and evicts the least-recently-viewed first.
export const MAX_CACHE_ENTRIES = 3;

// stableKey -> { buffer }
const byteCache = new Map();

// In-flight dedup: two components (or two rapid retries) asking for the same
// document at the same time share one fetch.
const pendingFetches = new Map();

// A PDF header must appear within the first 1024 bytes (PDF 1.7 s7.5.2; pdf.js
// tolerates the same leading slack). We check it because an HTTP 200 is NOT proof
// we got a PDF: an expired pre-signed URL returns a 403 whose body is an S3
// <Error> XML document, and fetch() resolves happily for any status. Validating
// here — the single choke point every consumer goes through — keeps a non-PDF body
// out of the cache, out of the viewer, and (the reason this exists) off the user's
// disk under a .pdf name. A statement that is silently an error document can be
// filed as evidence before anyone opens it.
const PDF_HEADER_SCAN_BYTES = 1024;
const PDF_HEADER = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-

const looksLikePdf = (buffer) => {
    const head = new Uint8Array(buffer, 0, Math.min(buffer.byteLength, PDF_HEADER_SCAN_BYTES));
    outer: for (let i = 0; i + PDF_HEADER.length <= head.length; i += 1) {
        for (let j = 0; j < PDF_HEADER.length; j += 1) {
            if (head[i + j] !== PDF_HEADER[j]) continue outer;
        }
        return true;
    }
    return false;
};

const evictIfNeeded = () => {
    while (byteCache.size > MAX_CACHE_ENTRIES) {
        // Map iteration order is insertion order; the first key is the LRU entry.
        byteCache.delete(byteCache.keys().next().value);
    }
};

/**
 * Get the raw bytes for a document, from cache or the network.
 *
 * @param {string} stableKey - document identity (cache key, e.g.
 *   stableDocumentKeyFromUrl output)
 * @param {string} url - the (signed) URL to actually fetch on a cache miss. A
 *   re-signed URL for the same object shares one cache entry.
 * @param {number} [retryNonce=0] - bump on retry to force a fresh fetch even if
 *   the browser's HTTP cache is holding a stale bad response (it also cache-busts
 *   the browser cache via `no-store`). A FAILED fetch drops its entry from
 *   `byteCache`, so retry 0 usually suffices.
 * @returns {Promise<ArrayBuffer>}
 * @throws if the response status is not OK, or if the body is not a PDF —
 *   callers may therefore treat a resolved value as real PDF bytes.
 */
export const getPdfBytes = async (stableKey, url, retryNonce = 0) => {
    const cached = byteCache.get(stableKey);
    if (cached) {
        byteCache.delete(stableKey);
        byteCache.set(stableKey, cached); // refresh LRU position
        return cached.buffer;
    }
    if (pendingFetches.has(stableKey)) {
        return pendingFetches.get(stableKey);
    }
    const task = (async () => {
        const response = await fetch(url, {
            cache: retryNonce ? 'no-store' : 'default',
        });
        if (!response.ok) {
            throw new Error(`PDF fetch failed: ${response.status} ${response.statusText || ''}`.trim());
        }
        const buffer = await response.arrayBuffer();
        if (!looksLikePdf(buffer)) {
            // Most likely an expired signed URL that S3 answered with an XML error
            // body, or a proxy interstitial. Never cache it, never render it.
            throw new Error('PDF fetch returned a non-PDF response (the document link may have expired)');
        }
        byteCache.set(stableKey, { buffer });
        evictIfNeeded();
        return buffer;
    })();
    pendingFetches.set(stableKey, task);
    try {
        return await task;
    } finally {
        pendingFetches.delete(stableKey);
    }
};

/** Test hook — drop one entry (or the whole cache). */
export const clearPdfBytes = (stableKey) => {
    if (stableKey) {
        byteCache.delete(stableKey);
    } else {
        byteCache.clear();
    }
};

/**
 * Load a PDF for react-pdf's `<Document file=...>`.
 *
 * `file` is ALWAYS an object (never null): a placeholder `{ url }` while the
 * bytes are being fetched — which keeps react-pdf in its real "loading" state —
 * then a STABLE `{ data: ArrayBuffer }` object once the bytes land. The data
 * object is created exactly once per document identity (memoized on the stable
 * key), so a re-render or a re-signed URL for the same object never triggers a
 * second fetch or parse. The fetch itself is keyed on the stable identity too:
 * `url` is read from a ref, so URL churn alone never re-enters the effect.
 *
 * @param {string} stableKey - document identity (stableDocumentKeyFromUrl output);
 *   null/undefined while the URL hasn't arrived yet
 * @param {string} url - the (signed) URL to fetch on a cache miss
 * @returns {{ file: {url: string} | {data: ArrayBuffer}, isDocumentLoading: boolean, error: Error | null, retry: () => void }}
 */
export const usePdfDocumentSource = (stableKey, url) => {
    const [fetchResult, setFetchResult] = useState(null); // { file:{data} } | { error } | null
    // Internal nonce: retry() bumps it, forcing a fresh (cache-busting) fetch.
    const [retryNonce, setRetryNonce] = useState(0);

    // Fresh URL on every render; read inside the effect without being a dep, so a
    // re-signed URL (same stableKey) never re-enters the effect.
    const urlRef = useRef(url);
    urlRef.current = url;

    // Placeholder while bytes are in flight: a real `{ url }` source. pdf.js will
    // happily parse this for the first page while our byte fetch runs in parallel
    // (one extra first-load download per document per session; every later revisit
    // — the cached, previously-broken case — resolves from the in-memory cache
    // before the user can blink). Keeps react-pdf in its genuine "loading" state,
    // and it is the exact object shape the component's existing react-pdf mock
    // expects.
    //
    // Identity is keyed on the document identity ONLY (not the raw URL, not the
    // retry nonce): a re-signed URL for the same object — or a retry — must not
    // change the placeholder, because react-pdf resets its source whenever the
    // `file` identity changes (which would trigger a second first-page fetch).
    //
    // The one exception: a failure is only final for the URL that produced it. useGetSignedUrl's SWR
    // cache hands back the LAST signed URL for an object synchronously on remount —
    // possibly long expired — and only then re-signs in the background. The fetch
    // below fires on that first render, so a revisit after the URL aged out (and
    // after the bytes fell out of the LRU) 403s even though a fresh URL lands a
    // moment later. Once a different URL for the same document arrives, the old
    // failure is stale: stay in "loading" and re-fetch with the new URL (effect
    // below). The placeholder is re-pointed at the fresh URL too, because the old
    // one is the URL that just 403'd and pdf.js would only re-request it.
    // Bounded: one attempt per distinct URL, never a loop.
    const awaitingFreshUrlRetry = !!(fetchResult?.error && url && fetchResult.failedUrl !== url);
    const placeholderRef = useRef(null);
    if (
        !placeholderRef.current ||
        placeholderRef.current.stableKey !== stableKey ||
        (awaitingFreshUrlRetry && placeholderRef.current.file.url === fetchResult.failedUrl)
    ) {
        placeholderRef.current = { stableKey, file: { url: urlRef.current } };
    }
    const placeholderFile = placeholderRef.current.file;

    const errorFile = useMemo(
        // react-pdf renders our `error` node only when its own parse rejects, and
        // it only re-parses when the `file` identity changes — so after a fetch
        // failure we hand it this unique (invalid-PDF) source: parsing rejects,
        // `pdf === false`, and the error node (message + Retry) shows. The `data:`
        // scheme keeps the browser from making a real network request for it.
        () => ({ data: `data:application/pdf;base64,AA==?nonce=${retryNonce}` }),
        [retryNonce]
    );

    // Reset the async result when a genuinely different document arrives, done
    // during the render pass (React's "adjust state on prop change" pattern) so
    // the first render of the new document already derives the placeholder — not
    // the previous document's bytes.
    const [prevKey, setPrevKey] = useState(stableKey);
    if (prevKey !== stableKey) {
        setPrevKey(stableKey);
        setFetchResult(null);
    }

    // `file` is a pure function of the current document + fetch progress:
    //   no document            -> null          (react-pdf "no data")
    //   bytes in flight         -> placeholder   (react-pdf "loading")
    //   fetch failed            -> errorFile     (react-pdf error node: msg + Retry)
    //   bytes landed            -> stable {data} (the cached ArrayBuffer)
    // The landed object lives in state, so its identity is stable across renders
    // and react-pdf never re-parses an already-loaded document.
    let file;
    if (!stableKey) {
        file = null;
    } else if (awaitingFreshUrlRetry) {
        file = placeholderFile;
    } else if (fetchResult && fetchResult.error) {
        file = errorFile;
    } else if (fetchResult && fetchResult.file) {
        file = fetchResult.file;
    } else {
        file = placeholderFile;
    }
    // Still true while there is no document at all (URL hasn't arrived yet), so
    // callers that gate on it (scroll-into-view, page input) behave as before.
    const isDocumentLoading = awaitingFreshUrlRetry || (!fetchResult?.file && !fetchResult?.error);
    const error = awaitingFreshUrlRetry ? null : fetchResult?.error || null;

    // The fetch. Runs once per (document, nonce) pair — never on URL churn alone —
    // and hands back either the cached bytes or the network ones.
    const fetchedRef = useRef(null); // { key, nonce }
    useEffect(() => {
        if (!stableKey) {
            fetchedRef.current = null;
            return undefined;
        }
        if (fetchedRef.current && fetchedRef.current.key === stableKey && fetchedRef.current.nonce === retryNonce) {
            return undefined; // this (document, attempt) already has a load done/in flight
        }
        fetchedRef.current = { key: stableKey, nonce: retryNonce };
        let cancelled = false;
        const attemptedUrl = urlRef.current;
        getPdfBytes(stableKey, attemptedUrl, retryNonce)
            .then((buffer) => {
                if (cancelled) return;
                // pdf.js TRANSFERS the ArrayBuffer it parses to its worker, which
                // DETACHES the main-thread copy. If we handed react-pdf the cached
                // master directly, the master would be detached after the first
                // parse — and the next consumer (a revisit, or a second <Document>
                // on the same screen: DocumentSplitEditor renders the doc as both a
                // thumbnail tree and a side panel) would get a detached buffer and
                // throw "Cannot perform Construct on a detached ArrayBuffer". Hand
                // each <Document> its OWN copy so the cached master stays intact.
                setFetchResult({ file: { data: buffer.slice(0) } });
            })
            .catch((err) => {
                if (cancelled) return;
                setFetchResult({ error: err, failedUrl: attemptedUrl });
                console.error(`Error loading PDF bytes for ${stableKey}:`, err);
            });
        return () => {
            cancelled = true;
        };
    }, [stableKey, retryNonce]);

    // Re-fetch with the fresh URL (see awaitingFreshUrlRetry). Bumping the nonce
    // also bypasses the browser HTTP cache, same as a manual Retry.
    useEffect(() => {
        if (awaitingFreshUrlRetry) {
            setFetchResult(null);
            setRetryNonce((n) => n + 1);
        }
    }, [awaitingFreshUrlRetry]);

    const retry = useCallback(() => {
        setRetryNonce((n) => n + 1);
    }, []);

    return { file, isDocumentLoading, error, retry };
};

export default usePdfDocumentSource;
